// Reglas de peso de la tienda online (súper). Función pura, sin cablear.
//
// El peso viaja como entero en milésimas de la unidad del producto (`weightMilli`);
// `weight = weightMilli / 1000` en `items.weight_unit`. Todo el chequeo de paso y de
// tope es aritmética entera (nada de módulo sobre decimales).
//
// Solo se llama con la señal retail prendida; un producto `each` (o sin modo) sin
// peso devuelve `{ ok: true, weight: null }` y el camino de restaurante no cambia.

export type OnlineWeightUnit = "lb" | "kg" | "g"

// Paso = mínimo y también el incremento; tope = lo más que se pide en una línea.
// lb: 0,25 lb y 25 lb · kg: 0,1 kg y 10 kg · g: 100 g y 10 kg.
export const ONLINE_WEIGHT_RULES: Record<OnlineWeightUnit, { stepMilli: number; maxMilli: number }> = {
  lb: { stepMilli: 250, maxMilli: 25_000 },
  kg: { stepMilli: 100, maxMilli: 10_000 },
  g: { stepMilli: 100_000, maxMilli: 10_000_000 },
}

export type OnlineWeightError = "weight_required" | "weight_not_allowed" | "weight_invalid"

export type OnlineWeightResult =
  | { ok: true; weight: number | null }
  | { ok: false; error: OnlineWeightError; message: string }

function fail(error: OnlineWeightError, message: string): OnlineWeightResult {
  return { ok: false, error, message }
}

export function validateOnlineWeight(args: {
  pricingMode: string | null | undefined
  weightUnit: string | null | undefined
  weightMilli: number | undefined
  quantity: number
}): OnlineWeightResult {
  const { pricingMode, weightUnit, weightMilli, quantity } = args

  // Producto por unidad: no lleva peso.
  if (pricingMode !== "weight") {
    if (weightMilli !== undefined) {
      return fail("weight_not_allowed", "Este producto no se vende por peso.")
    }
    return { ok: true, weight: null }
  }

  // Producto por peso.
  if (weightMilli === undefined) {
    return fail("weight_required", "Elige cuánto quieres.")
  }
  if (
    typeof weightMilli !== "number" ||
    !Number.isInteger(weightMilli) ||
    weightMilli <= 0 ||
    quantity !== 1
  ) {
    return fail("weight_invalid", "Esa cantidad no es válida.")
  }
  if (typeof weightUnit !== "string" || !Object.hasOwn(ONLINE_WEIGHT_RULES, weightUnit)) {
    return fail("weight_invalid", "Este producto no se puede pedir por peso en línea.")
  }
  const { stepMilli, maxMilli } = ONLINE_WEIGHT_RULES[weightUnit as OnlineWeightUnit]
  if (weightMilli % stepMilli !== 0) {
    return fail("weight_invalid", "Esa cantidad no es válida.")
  }
  if (weightMilli > maxMilli) {
    return fail("weight_invalid", "Esa cantidad es demasiado grande para un pedido en línea.")
  }
  return { ok: true, weight: weightMilli / 1000 }
}
